import { ErrorCode } from '@gr/core/errors';
import { ResultHelper, type OperationResult } from '@gr/utils/operation-result';

type Query = Record<string, string | number | boolean | undefined | null>;

export function withQuery(url: string, query?: Query): string {
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `${url}?${text}` : url;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

/**
 * Обёртка над fetch: всегда возвращает OperationResult (сетевые сбои и 5xx —
 * тоже). 401 сообщается подписчику (IAM-UC-3.CC-1) — тот уводит на вход.
 */
export class HttpClient {
  private unauthorizedHandler: (() => void) | null = null;

  constructor(private readonly fetchImpl: (input: string, init?: RequestInit) => Promise<Response> = (input, init) => fetch(input, init)) {}

  onUnauthorized(handler: () => void): void {
    this.unauthorizedHandler = handler;
  }

  /** signal — отмена запроса, ответ на который уже не нужен (поиск в автокомплите). */
  get<T>(url: string, query?: Query, signal?: AbortSignal): Promise<OperationResult<T>> {
    return this.request<T>('GET', withQuery(url, query), undefined, signal);
  }

  post<T>(url: string, body: unknown = {}): Promise<OperationResult<T>> {
    return this.request<T>('POST', url, body);
  }

  put<T>(url: string, body: unknown = {}): Promise<OperationResult<T>> {
    return this.request<T>('PUT', url, body);
  }

  patch<T>(url: string, body: unknown = {}): Promise<OperationResult<T>> {
    return this.request<T>('PATCH', url, body);
  }

  delete<T>(url: string, query?: Query): Promise<OperationResult<T>> {
    return this.request<T>('DELETE', withQuery(url, query));
  }

  /** Файл с сервера как Blob (пакет публикации: поделиться фото с телефона). */
  async blob(url: string): Promise<OperationResult<Blob | null>> {
    try {
      const response = await this.fetchImpl(url, { method: 'GET', credentials: 'same-origin' });
      if (response.status === 401) this.unauthorizedHandler?.();
      return response.ok ? ResultHelper.Ok(await response.blob()) : ResultHelper.Fail(null, [response.status === 404 ? ErrorCode.NotFound : ErrorCode.InternalError]);
    } catch {
      return ResultHelper.Fail(null, [ErrorCode.InternalError]);
    }
  }

  private async request<T>(method: string, url: string, body?: unknown, signal?: AbortSignal): Promise<OperationResult<T>> {
    try {
      const response = await this.fetchImpl(url, {
        method,
        credentials: 'same-origin',
        headers: body === undefined ? { accept: 'application/json' } : { accept: 'application/json', 'content-type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      });
      return this.parse<T>(response.status, await response.text());
    } catch {
      // Сеть недоступна или запрос отменён (signal) — отменённый ответ вызывающий отбросит сам.
      return ResultHelper.Fail(null as T, [ErrorCode.InternalError]);
    }
  }

  /** MED-UC-1: файл уходит «сырым» телом; XHR даёт прогресс загрузки. */
  upload<T>(url: string, file: File, onProgress?: (progress: UploadProgress) => void): Promise<OperationResult<T>> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.withCredentials = true;
      xhr.setRequestHeader('content-type', file.type || 'application/octet-stream');
      xhr.setRequestHeader('x-file-name', encodeURIComponent(file.name));
      xhr.upload.onprogress = (event) => onProgress?.({ loaded: event.loaded, total: event.lengthComputable ? event.total : file.size });
      xhr.onload = () => resolve(this.parse<T>(xhr.status, xhr.responseText));
      xhr.onerror = () => resolve(ResultHelper.Fail(null as T, [ErrorCode.InternalError]));
      xhr.send(file);
    });
  }

  private parse<T>(status: number, text: string): OperationResult<T> {
    if (status === 401) this.unauthorizedHandler?.();
    try {
      const parsed = JSON.parse(text) as OperationResult<T>;
      if (typeof parsed === 'object' && parsed !== null && 'success' in parsed) return parsed;
      return ResultHelper.Ok(parsed as T);
    } catch {
      return ResultHelper.Fail(null as T, [status === 413 ? ErrorCode.MediaTooLarge : ErrorCode.InternalError]);
    }
  }
}
